import PaymentDateWarning from './PaymentDateWarning';
import {drawStudentReceipt} from '../utils/studentReceipt';
import WelcomeKitDownload from './WelcomeKitDownload';
import {appendWelcomeLetter,welcomeFilename} from '../utils/welcomeKit';
import EventPaymentHistory, { useEventPaymentHistory } from './EventPaymentHistory';
import { studentFee, admissionFeeMonth } from '../utils/studentFees';
import { toPaise, fromPaise, sumPaise } from '../utils/inr';
import { monthlyFine, firstChargeableMonth } from '../utils/studentFeePolicy';
import { todayKolkata, validPaymentDay } from '../utils/paymentDates';
import { studentFeeMonth, studentPaymentOption, activeStudent, validFeeMonth } from '../utils/studentFeeMonths';
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import AsyncSelect from "react-select/async";
const API_BASE = (window.__APP_CONFIG__ && window.__APP_CONFIG__.API_URL) || process.env.REACT_APP_API_URL || '';
if (API_BASE && !axios.defaults.baseURL) {
  axios.defaults.baseURL = API_BASE;
}


const AddPaymentForm = () => {
  const [lastWelcomePayment,setLastWelcomePayment]=useState(null);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [studentOptions, setStudentOptions] = useState([]);
  const [paymentDate, setPaymentDate] = useState(
    todayKolkata()
  );
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  // Misc Fee fields
  const [miscAmount, setMiscAmount] = useState(0);
  const [miscNote, setMiscNote] = useState("");
  const [miscDiscount, setMiscDiscount] = useState(0);

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const eventHistory = useEventPaymentHistory(selectedStudent?.value || selectedStudent, selectedPaymentType === 'Event' ? selectedEvent : '');
  const eventRequestRef = useRef(null);
  const eventSubmitRef = useRef(false);

  const [eventFee, setEventFee] = useState(0);
  const [eventDiscount, setEventDiscount] = useState(0);

  const [studentFeeDetails, setStudentFeeDetails] = useState(null);
  const [monthYear, setMonthYear] = useState("");
  const [lateFee, setLateFee] = useState(0);
  const [monthlyDiscount, setMonthlyDiscount] = useState(0);
  const [admissionDiscount, setAdmissionDiscount] = useState(0);

  // New states as per instruction
  const [hasMonthlyFee, setHasMonthlyFee] = useState(false);
  const [monthlyFeeAmount, setMonthlyFeeAmount] = useState(0);
  const [admissionFeeDue, setAdmissionFeeDue] = useState(false);
  const [admissionFeeAmount, setAdmissionFeeAmount] = useState(0);
  // One-time course fee states
  const [hasOnetimeFee, setHasOnetimeFee] = useState(false);
  const [onetimeAmount, setOnetimeAmount] = useState(0);
  const [workshops, setWorkshops] = useState([]);
  const [workshopId, setWorkshopId] = useState('');
  const [onetimeDiscount, setOnetimeDiscount] = useState(0);
// Global payment mode (required) and event note (editable)
const [paymentMode, setPaymentMode] = useState("UPI");
const [eventNote, setEventNote] = useState("Event Fee");

  // --- Confirmation modal state ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null); // { feeLines, discountAtPayment, gross, total }

  // Helpers for preview
  const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const safeDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '-');
  const studentLabel = (s) =>
    s ? `${s.name || s.label || '-'}${s.studentId ? ` (${s.studentId})` : ''}` : '-';

  /**
   * Build feeLines and discount based on the current form state.
   * Returns { feeLines, discountAtPayment, gross, total } or throws Error with a message.
   */
const formatMonthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(n => parseInt(n, 10));
  if (isNaN(y) || isNaN(m)) return ym;
  try {
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  } catch {
    return ym;
  }
};

// Helper: get YYYY-MM-01 string for the first day of the given month-year (YYYY-MM or MM-YYYY)


// Helper: get month start/end Date objects from "YYYY-MM" or "MM-YYYY"
const getMonthRange = (ym) => {
  if (!ym || typeof ym !== 'string') return null;
  const parts = ym.split('-');
  let y, m;
  if (parts.length !== 2) return null;
  if (parts[0].length === 4) { y = parseInt(parts[0],10); m = parseInt(parts[1],10); }
  else { m = parseInt(parts[0],10); y = parseInt(parts[1],10); }
  if (!y || !m || m < 1 || m > 12) return null;
  const start = new Date(y, m - 1, 1);
  const end   = new Date(y, m, 1); // exclusive
  return { start, end };
};

// Check if a monthly payment already exists for student+month.
// Returns { exists: boolean, found: payment|null }
const hasMonthlyPaymentAlready = async (studentId, ym) => {
  const range = getMonthRange(ym);
  if (!studentId || !range) return { exists:false, found:null };

  try {
    // Prefer server-side filter if available
    const res = await axios.get('/api/payments', { params: { studentId } });
    const raw = Array.isArray(res.data) ? res.data : (res.data?.payments || []);

    // 🔒 Important: filter to *this* student in case the backend ignores the query param
    const relevant = raw.filter(p => {
      const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
      return String(sid) === String(studentId);
    });

    for (const p of relevant) {
      // Any monthly line for the requested month?
      const lines = Array.isArray(p.feeLines) ? p.feeLines : [];
      for (const fl of lines) {
        if (fl?.type === 'monthlyFee') {
          // If we have explicit month tag, match directly
          if (studentFeeMonth(p) === ym) {
            return { exists:true, found:p };
          }

        }
      }
    }
    return { exists:false, found:null };
  } catch (e) {
    // Fallback: try fetching all (if API doesn't support studentId param)
    try {
      const resAll = await axios.get('/api/payments');
      const all = Array.isArray(resAll.data) ? resAll.data : (resAll.data?.payments || []);
      const relevant = all.filter(p => {
        const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
        return String(sid) === String(studentId);
      });
      for (const p of relevant) {
        const lines = Array.isArray(p.feeLines) ? p.feeLines : [];
        for (const fl of lines) {
          if (fl?.type === 'monthlyFee') {
            if (studentFeeMonth(p) === ym) return { exists:true, found:p };

          }
        }
      }
      return { exists:false, found:null };
    } catch (e2) {
      console.warn('Duplicate check failed; proceeding without blocking:', e2?.message || e2);
      return { exists:false, found:null };
    }
  }
};

  const monthlyAgreement = studentFee(studentFeeDetails || {}, 'monthly', monthYear || todayKolkata().slice(0, 7));
  const admissionAgreement = studentFee(studentFeeDetails || {}, 'admission', admissionFeeMonth(studentFeeDetails || {}));
  const needsMonthlyFee = ['MonthlyOnly', 'AdmissionPlusFirstMonth'].includes(selectedPaymentType);
  const needsAdmissionFee = ['AdmissionOnly', 'AdmissionPlusFirstMonth'].includes(selectedPaymentType);
  const missingMonthlyFee = needsMonthlyFee && monthlyAgreement.amount == null;
  const missingAdmissionFee = needsAdmissionFee && admissionAgreement.amount == null;
  const feeSetupRequired = missingMonthlyFee || missingAdmissionFee;

  const buildFeeLinesAndDiscount = () => {
    if (['MonthlyOnly', 'AdmissionPlusFirstMonth'].includes(selectedPaymentType) && studentFee(studentFeeDetails || {}, 'monthly', monthYear).amount == null) throw new Error('Confirm the student monthly fee for this month in Manage Students.');
    if (['AdmissionOnly', 'AdmissionPlusFirstMonth'].includes(selectedPaymentType) && studentFee(studentFeeDetails || {}, 'admission', admissionFeeMonth(studentFeeDetails || {})).amount == null) throw new Error('Confirm the student admission fee for the joining month in Manage Students.');

    if (!selectedPaymentType) throw new Error('Please select a payment type.');

    let feeLines = [];
    let discountAtPayment = 0;

    if (selectedPaymentType === "MonthlyOnly") {
      if (!hasMonthlyFee) throw new Error("This student does not have a monthly course.");
      if (!validFeeMonth(monthYear)) throw new Error("Please choose the month (YYYY-MM).");
      const firstMonth = firstChargeableMonth(studentFeeDetails || {});
      if (firstMonth && monthYear < firstMonth) throw new Error(`First chargeable month is ${firstMonth}.`);

      feeLines.push({
    type: "monthlyFee",
    month: monthYear,
    note: `Monthly Fee (${formatMonthLabel(monthYear)})`,
    amount: Number(monthlyFeeAmount) || 0,
  });

      if ((Number(lateFee) || 0) > 0) {
        feeLines.push({ type: "lateFee", amount: Number(lateFee) || 0 });
      }

      discountAtPayment = Number(monthlyDiscount) || 0;
    }

    if (selectedPaymentType === "AdmissionOnly") {
      if (!admissionFeeDue) throw new Error("Admission fee already paid.");
      const amt = Number(admissionFeeAmount) || 0;
      if (amt < 0) throw new Error("Invalid admission fee amount.");
      feeLines.push({ type: "admissionFee", amount: amt, note: `Admission Fee (${paymentDate})` });
      discountAtPayment = Number(admissionDiscount) || 0;
    }

    if (selectedPaymentType === "AdmissionPlusFirstMonth") {
      if (!admissionFeeDue || !hasMonthlyFee) {
        throw new Error("This option is only for first-time monthly students.");
      }
      if (!validFeeMonth(monthYear)) throw new Error("Please choose the month (YYYY-MM).");
      const firstMonth = firstChargeableMonth(studentFeeDetails || {});
      if (firstMonth && monthYear < firstMonth) throw new Error(`First chargeable month is ${firstMonth}.`);

      const admAmt = Number(admissionFeeAmount) || 0;
      if (admAmt < 0) throw new Error("Invalid admission fee amount.");
      feeLines.push({ type: "admissionFee", amount: admAmt, note: `Admission Fee (${paymentDate})` });

      const mAmt = Number(monthlyFeeAmount) || 0;
      if (mAmt < 0) throw new Error("Invalid monthly fee amount.");
      feeLines.push({ type: "monthlyFee", month: monthYear, note: `Monthly Fee (${formatMonthLabel(monthYear)})`, amount: mAmt });
      if (lateFee > 0) feeLines.push({ type: "lateFee", amount: lateFee });

      discountAtPayment = Number(monthlyDiscount) || 0;
    }

    if (selectedPaymentType === "OneTime") {
  if (!hasOnetimeFee) throw new Error("This student does not have a one-time course.");
  if (!workshops.some(c => String(c._id) === workshopId)) throw new Error("Select an enrolled workshop.");
      feeLines.push({ type: "onetimeFee", courseId: workshopId, amount: Number(onetimeAmount) || 0, note: `Workshop Fee - ${workshops.find(c => String(c._id) === workshopId)?.name}` });
  discountAtPayment = Number(onetimeDiscount) || 0;
}

    if (selectedPaymentType === "Event") {
      if (!selectedEvent) throw new Error("Please select an event.");
      const amt = Number(eventFee) || 0;
      if (amt <= 0) throw new Error("Selected event has no valid fee.");
      feeLines.push({
        type: "eventFee",
        eventId: selectedEvent,
        amount: amt,
        note: (eventNote || "Event Fee").trim(),
      });
      discountAtPayment = Number(eventDiscount) || 0;
    }

    if (selectedPaymentType === "Miscellaneous") {
      if ((Number(miscAmount) || 0) <= 0) throw new Error("Amount must be greater than zero.");
      if (!miscNote.trim()) throw new Error("Description / Notes cannot be empty.");
      feeLines.push({ type: "miscFee", amount: Number(miscAmount) || 0, note: miscNote.trim() });
      discountAtPayment = Number(miscDiscount) || 0;
    }

    if (feeLines.length === 0) throw new Error("No payment items to submit.");

    const grossPaise = sumPaise(feeLines.map(line => toPaise(line.amount, 'Fee amount')));
    const gross = fromPaise(grossPaise);
    const total = fromPaise(Math.max(grossPaise - toPaise(discountAtPayment, 'Discount'), 0));

    return { feeLines, discountAtPayment, gross, total };
  };

  

  // Remove fetching all students on mount - handled by async select

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get("/api/events");
        if (!res.data || res.data.length === 0) {
          if (res) {
            console.error("Fetched events response exists but no data found:", res);
          }
          console.warn("No events returned from API.");
        }
        console.log("Fetched Events:", res.data); // Debug
        const activeEvents = (res.data || []).filter(event => event.isActive);
        setEvents(activeEvents);
      } catch (error) {
        console.error("Failed to fetch events", error);
        setEvents([]);
      }
    };
    fetchEvents();
  }, []);

  // Helper: map a student doc to AsyncSelect option, with __active flag

  // Prefetch some active students so the dropdown isn't empty
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer a backend that supports filtering; fallback to all.
        const res = await axios.get('/api/students', { params: { status: 'active' } });
        const arr = Array.isArray(res.data) ? res.data : [];
        // Treat missing status as active; exclude only explicit inactive
        const likelyActive = arr.filter(s => activeStudent(s));
        const options = likelyActive
          .sort((a,b) => String(a.name||'').localeCompare(String(b.name||'')))
          .map(studentPaymentOption);
        if (!cancelled) setStudentOptions(options);
      } catch (e) {
        console.warn('Prefetch students failed, continue with search-only', e?.message || e);
        if (!cancelled) setStudentOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load full student details when selectedStudent changes
  // Load full student details when selectedStudent changes
  useEffect(() => {
    let cancelled = false;
    if (!selectedStudent) {
      setStudentFeeDetails(null);
      setMonthYear("");
      setLateFee(0);
      setMonthlyDiscount(0);
      setAdmissionDiscount(0);
        setHasMonthlyFee(false);
      setMonthlyFeeAmount(0);
      setAdmissionFeeDue(false);
      setAdmissionFeeAmount(0);
      setHasOnetimeFee(false);
      setOnetimeAmount(0);
      setOnetimeDiscount(0);
      return;
    }

    const fetchStudentDetails = async () => {
      try {
        // Changed line as per instruction:
        const studentId = selectedStudent.value || selectedStudent;
        const response = await axios.get(`/api/students/${studentId}`);
        if (cancelled) return;
        const student = response.data;
        if (student) {
          // Determine if course is monthly and set amounts (with specialMonthlyFee override)
          const isMonthly = !!(student.course && student.course.feeType === "monthly");
          setHasMonthlyFee(isMonthly);
          setMonthlyFeeAmount(studentFee(student, 'monthly', todayKolkata().slice(0, 7)).amount ?? 0);

          // Admission fee paid flag comes from backend as `admissionFeePaid`
          const paidFlag = student?.admissionFeePaid;
          const isPaid = paidFlag === true || paidFlag === 1 || paidFlag === "1";
          setAdmissionFeeDue(!isPaid);

          // Resolve admission fee amount from student first, then course
          setAdmissionFeeAmount(studentFee(student, 'admission', admissionFeeMonth(student)).amount ?? 0);

          // One-time course detection & amount
          const choices = [student.course, ...(student.additionalCourses || [])].filter((c,i,all) => c?.feeType === 'onetime' && all.findIndex(other => String(other?._id) === String(c._id)) === i);
          setWorkshops(choices);
          setWorkshopId(String(choices[0]?._id || ''));
          setHasOnetimeFee(choices.length > 0);
          setOnetimeAmount(choices[0]?.onetimeFee ?? 0);
          setOnetimeDiscount(0);

          setStudentFeeDetails(student);
        } else {
          setHasMonthlyFee(false);
          setMonthlyFeeAmount(0);
          setAdmissionFeeDue(false);
          setAdmissionFeeAmount(0);
          setHasOnetimeFee(false);
          setOnetimeAmount(0);
          setOnetimeDiscount(0);
          setStudentFeeDetails(null);
        }
        setMonthYear("");
        setLateFee(0);
        setMonthlyDiscount(0);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch student details", error);
        setHasMonthlyFee(false);
        setMonthlyFeeAmount(0);
        setAdmissionFeeDue(false);
        setAdmissionFeeAmount(0);
        setHasOnetimeFee(false);
        setOnetimeAmount(0);
        setOnetimeDiscount(0);
        setStudentFeeDetails(null);
      }
    };

    fetchStudentDetails();
    return () => { cancelled = true; };
  }, [selectedStudent]);

  useEffect(() => {
    const eventObj = events?.find((e) => e._id === selectedEvent);
    console.log("Selected Event:", selectedEvent, "Resolved Fee:", eventObj?.eventFee); // Debug
    setEventFee(eventObj?.eventFee ?? 0);
  }, [selectedEvent, events]);

  useEffect(() => {
    if (studentFeeDetails) setMonthlyFeeAmount(studentFee(studentFeeDetails, 'monthly', monthYear || todayKolkata().slice(0, 7)).amount ?? 0);
  }, [studentFeeDetails, monthYear]);

  useEffect(() => {
    setLateFee(monthlyFine(monthYear, paymentDate).amount);
  }, [monthYear, paymentDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const studentId = selectedStudent?.value || selectedStudent;
    if (!studentId) {
      alert("Please select a student.");
      return;
    }
    if (!validPaymentDay(paymentDate)) {
      alert("Please select a payment date.");
      return;
    }
    if (!selectedPaymentType) {
      alert("Please select a payment type.");
      return;
    }

    try {
      if (selectedPaymentType === 'Event' && !eventHistory.ready) throw new Error('Wait for previous event payments to load, or retry the history check.');
      const built = buildFeeLinesAndDiscount();
      // If any monthly fee in this submission, block duplicates
      const hasMonthly = (built.feeLines || []).some(fl => fl.type === 'monthlyFee');
      if (hasMonthly) {
        const ym = (built.feeLines || []).find(fl => fl.type === 'monthlyFee')?.month || monthYear;
        const sid = selectedStudent?.value || selectedStudent;
        const dup = await hasMonthlyPaymentAlready(sid, ym);
        if (dup.exists) {
          alert('A monthly payment for this student and month already exists.\n\n'
            + `Payment ID: ${dup.found?._id || ''}\n`
            + `Date: ${dup.found?.date ? new Date(dup.found.date).toLocaleDateString('en-IN') : ''}\n`
            + 'Please verify before proceeding.');
          return;
        }
      }
      setConfirmData(built);
      setShowConfirm(true);
    } catch (err) {
      alert(err.message || String(err));
    }
  };

  // Student async search function for react-select AsyncSelect (returns a Promise of options)
  const loadStudentOptions = async (inputValue) => {
    const q = (inputValue || '').trim();

    // If no input, show all preloaded active students
    if (!q) return studentOptions;

    // First try the backend search (should already filter by active + name/phone/id)
    try {
      const res = await axios.get('/api/students/search', {
        params: { q, status: 'active' }
      });
      const items = Array.isArray(res.data) ? res.data : [];
      if (items.some(student => student.status === undefined)) throw new Error('Search API lacks active-status fields; verify the full active list.');
      const opts = items.filter(activeStudent).map(studentPaymentOption);
      return opts;
    } catch (err) {
      // Fallback: pull active students and filter locally on name, studentId, or phone
      try {
        const resAll = await axios.get('/api/students', { params: { status: 'active' } });
        const list = Array.isArray(resAll.data) ? resAll.data : [];
        // Treat missing status as active; exclude only explicit inactive
        const likelyActive = list.filter(s => activeStudent(s));
        const needle = q.toLowerCase();
        const filtered = likelyActive
          .filter(s => {
            const name = String(s.name || '').toLowerCase();
            const sid  = String(s.studentId || '').toLowerCase();
            const ph   = String(s.phone || '').toLowerCase();
            const wa = String(s.whatsapp || '').toLowerCase();
            return name.includes(needle) || sid.includes(needle) || ph.includes(needle) || wa.includes(needle);
          })
          .sort((a,b) => {
            const av = String(a.name||'').toLowerCase();
            const bv = String(b.name||'').toLowerCase();
            const aStarts = av.startsWith(needle) ? 0 : 1;
            const bStarts = bv.startsWith(needle) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return av.localeCompare(bv);
          })
          .map(studentPaymentOption);
        return filtered;
      } catch (e2) {
        console.error('Error loading students:', e2);
        return [];
      }
    }
  };

  // Handler for selecting a student from AsyncSelect
  const handleStudentSelect = (option) => {
    if (option && option.__active === false) {
      alert('Inactive students cannot be selected for payments.');
      return;
    }
    setSelectedStudent(option);
    setSelectedPaymentType(""); // Reset payment type
    setStudentFeeDetails(null); // Reset student details
    setMonthYear(""); // Reset monthly fee section
    setLateFee(0);
    setMonthlyDiscount(0);
    setHasMonthlyFee(false);
    setMonthlyFeeAmount(0);
    setAdmissionFeeDue(false);
    setHasOnetimeFee(false);
    setOnetimeAmount(0);
    setOnetimeDiscount(0);
    setPaymentMode("UPI");
    setEventNote("Event Fee");
  };

  const confirmAndSubmit = async () => {
    if (eventSubmitRef.current) return;
    eventSubmitRef.current = true;
    try {
      setIsSubmitting(true);
      const studentId = selectedStudent?.value || selectedStudent;
      const { feeLines, discountAtPayment, total } = confirmData || {};
      if (!studentId || !feeLines) {
        alert('Missing data to submit.');
        return;
      }

      // Adjust payment date: if paying for a future month, shift to the 1st of that month
      const effectivePaymentDate = paymentDate;

      const payload = {
        studentId,
        date: effectivePaymentDate,
        expectedFeeVersion: studentFeeDetails?.feeVersion || 0,
        ...(feeLines.some(line=>line.type==='monthlyFee') ? { feeMonth: monthYear } : {}),
        paymentMode,
        discount: discountAtPayment,
        feeLines,
        totalAmount: total,
      };

      if (feeLines.some(line => line.type === 'eventFee')) {
        const signature = JSON.stringify({ ...payload, expectedFeeVersion: undefined });
        if (!eventRequestRef.current || eventRequestRef.current.signature !== signature) eventRequestRef.current = { signature, key: window.crypto.randomUUID() };
      }
      const res = await axios.post("/api/payments", payload, { headers: eventRequestRef.current ? { 'X-Idempotency-Key': eventRequestRef.current.key } : {} });

      eventRequestRef.current = null;
      eventHistory.reload();

      if(res.data?.welcomeKit)setLastWelcomePayment(res.data);
      let receiptDownloaded = true;
      try {
      const studentCode = studentFeeDetails?.studentId || '';
      const orgKey = studentFeeDetails?.course?.organization || 'Salankaraa';
      const fileSafeOrg = orgKey.replace(/\s+/g, '');
      const issueDate = effectivePaymentDate;
      const doc = new jsPDF({unit:'pt',format:'a4'});
      drawStudentReceipt(doc,{...res.data,feeLines:res.data?.feeLines||feeLines,discount:res.data?.discount??discountAtPayment,totalAmount:res.data?.totalAmount??total,paymentMode:res.data?.paymentMode||paymentMode,date:res.data?.date||effectivePaymentDate,feeMonth:res.data?.feeMonth||monthYear},studentFeeDetails||{}, {eventNameById:id=>events.find(e=>e._id===id)?.name});
      appendWelcomeLetter(doc,res.data?.welcomeKit);
      doc.save(welcomeFilename(res.data,`receipt_${fileSafeOrg}_${studentCode || studentId}_${issueDate}.pdf`));

      } catch { receiptDownloaded = false; }
      alert(receiptDownloaded ? (res.data?.welcomeKit ? 'Payment recorded. Welcome kit and receipt downloaded.' : 'Payment recorded and receipt downloaded.') : 'Payment recorded, but receipt download failed. Download the saved receipt from the payment report; do not submit this payment again.');

      try {
        const sid = selectedStudent?.value || selectedStudent;
        if (sid) {
          const refresh = await axios.get(`/api/students/${sid}`);
          setStudentFeeDetails(refresh.data || null);
          const isMonthly = !!(refresh.data?.course && refresh.data.course.feeType === "monthly");
          setHasMonthlyFee(isMonthly);
          setMonthlyFeeAmount(isMonthly ? (studentFee(refresh.data, 'monthly', monthYear || todayKolkata().slice(0, 7)).amount ?? 0) : 0);
          const paidRefresh = refresh.data?.admissionFeePaid;
          const paidNow = paidRefresh === true || paidRefresh === 1 || paidRefresh === "1";
          setAdmissionFeeDue(!paidNow);
        }
      } catch (_) {}

      setSelectedPaymentType("");
    } catch (err) {
      console.error("Payment submission failed:", err?.response?.data || err.message);
      if ((confirmData?.feeLines || []).some(line => line.type === 'eventFee')) { eventHistory.reload(); alert(`Event payment is not confirmed. Check the refreshed history before retrying. ${err?.response?.data?.error || err.message}`); }
      else alert(`❌ Payment submission failed. ${err?.response?.data?.error || err.message}`);
    } finally {
      eventSubmitRef.current = false;
      setIsSubmitting(false);
      setShowConfirm(false);
      setConfirmData(null);
    }
  };

  const cancelConfirm = () => {
    setShowConfirm(false);
    setConfirmData(null);
  };

// Dynamic payment type options based on selected student
const paymentTypeOptions = [];
if (hasMonthlyFee) {
  if (admissionFeeDue) {
    paymentTypeOptions.push({ value: "AdmissionOnly", label: "Admission Fee" });
    paymentTypeOptions.push({ value: "AdmissionPlusFirstMonth", label: "Admission + First Month" });
  } else {
    paymentTypeOptions.push({ value: "MonthlyOnly", label: "Monthly Fee" });
  }
}
if (hasOnetimeFee) {
  paymentTypeOptions.push({ value: "OneTime", label: "Workshop Fee" });
}
paymentTypeOptions.push({ value: "Event", label: "Event Fee" });
paymentTypeOptions.push({ value: "Miscellaneous", label: "Miscellaneous" });

  return (
    <div
      className="payment-form-container"
      style={{
        maxWidth: "600px",
        margin: "0 auto",
        padding: "2rem",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#fafafa"
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: "1.5rem" }}>Add Payment</h2>
      {lastWelcomePayment&&<WelcomeKitDownload payment={lastWelcomePayment}/>}

      <form onSubmit={handleSubmit}>
        <div className="form-group" style={{ marginBottom: "1rem" }}>
          <label htmlFor="student" style={{ display: "block", marginBottom: "0.5rem" }}>Student</label>
          <AsyncSelect
            inputId="student"
            cacheOptions
            defaultOptions={studentOptions}
            loadOptions={(val) => (val && val.trim().length >= 2 ? loadStudentOptions(val) : Promise.resolve(studentOptions))}
            placeholder="Search student by name/ID/phone… (type 2+ chars)"
            value={selectedStudent}
            isClearable
            loadingMessage={() => 'Searching…'}
            noOptionsMessage={() => 'No matching students'}
            menuPlacement="auto"
            onMenuOpen={() => { if (!studentOptions.length) { /* prefetch already attempted */ } }}
            filterOption={null}
            isOptionDisabled={(opt) => opt && opt.__active === false}
            onChange={(opt) => {
              if (opt && opt.__active === false) {
                alert('This student is inactive and cannot receive payments.');
                return;
              }
              handleStudentSelect(opt);
            }}
          />
        </div>

        <div className="form-section" style={{ marginBottom: "1.5rem" }}>
          <label htmlFor="paymentType" style={{ display: "block", marginBottom: "0.5rem" }}>Payment Type</label>
          <select
            id="paymentType"
            value={selectedPaymentType}
            onChange={(e) => {
              setSelectedPaymentType(e.target.value);
              if (e.target.value !== 'OneTime') {
                setOnetimeDiscount(0);
              }
            }}
            style={{ width: "100%", padding: "0.5rem" }}
          >
            <option value="">Select Payment Type</option>
            {paymentTypeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {selectedPaymentType && (
          <>
            {feeSetupRequired && <div role="alert" className="mb-4 rounded border border-amber-400 bg-amber-50 p-3 text-sm">
              <p><strong>Student fee not configured</strong></p>
              <p>{missingMonthlyFee ? `No confirmed monthly fee is recorded for ${monthYear || 'the selected month'}.` : 'No confirmed admission fee is recorded for this joining month.'} This does not mean the fee is zero.</p>
              {missingMonthlyFee && typeof studentFeeDetails?.course?.monthlyFee === 'number' && <p>Course suggestion: ₹{studentFeeDetails.course.monthlyFee.toLocaleString('en-IN')}. Confirm the student's agreed amount before payment.</p>}
              <p><a href="/students/list" target="_blank" rel="noreferrer" className="text-blue-700 underline">Manage Students</a> → Edit {studentFeeDetails?.studentId || 'student'} → Student Fees. Save the agreed amount with its effective month, then reselect the student and fee month here.</p>
            </div>}
            {selectedPaymentType === "MonthlyOnly" && hasMonthlyFee && (
              <>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="monthYear">
                    Fee Month (YYYY-MM)
                  </label>
                  <input
                    type="month"
                    id="monthYear"
                    name="monthYear"
                    value={monthYear || ""}
                    onChange={(e) => setMonthYear(e.target.value)}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Monthly Fee</label>
                  <input
                    type="number"
                    aria-label="Monthly fee amount"
                    value={monthlyAgreement.amount == null ? '' : monthlyFeeAmount}
                    placeholder="Fee not configured"
                    disabled
                    style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
                  />
                </div>


                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Late Fee (₹20/day after 12th; stops at fee month end)</label>
                  <input
                    type="number"
                    value={lateFee}
                    disabled
                    style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Discount</label>
                  <input
                    type="number"
                    value={monthlyDiscount}
                    onChange={(e) => setMonthlyDiscount(Number(e.target.value))}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                 <strong>
  Total Payable: {missingMonthlyFee ? 'Awaiting fee confirmation' : <>Rs.{Number(Math.max(
    (monthlyFeeAmount || 0) +
    (lateFee || 0) - (monthlyDiscount || 0),
    0
  )).toFixed(2)}</>}
</strong>
                </div>
              </>
            )}


            {selectedPaymentType === "AdmissionOnly" && admissionFeeDue && (
  <>
    <div className="form-group" style={{ marginBottom: "1rem" }}>
      <label>Admission Fee</label>
      <input
        type="number"
        value={admissionAgreement.amount == null ? '' : admissionFeeAmount}
                    placeholder="Fee not configured"
        disabled
        style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
      />
    </div>
    <div className="form-group" style={{ marginBottom: "1rem" }}>
      <label>Discount</label>
      <input
        type="number"
        value={admissionDiscount}
        onChange={(e) => setAdmissionDiscount(Number(e.target.value))}
        style={{ width: "100%", padding: "0.5rem" }}
      />
    </div>
    <div className="form-group" style={{ marginBottom: "1rem" }}>
      <strong>
        Total Payable: {missingAdmissionFee ? 'Awaiting fee confirmation' : <>Rs.{Number(Math.max((admissionFeeAmount || 0) - (admissionDiscount || 0), 0)).toFixed(2)}</>}
      </strong>
    </div>
  </>
)}

            {selectedPaymentType === "AdmissionPlusFirstMonth" && admissionFeeDue && hasMonthlyFee && (
              <>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="monthYearCombo">
                    Fee Month (YYYY-MM)
                  </label>
                  <input
                    type="month"
                    id="monthYearCombo"
                    name="monthYearCombo"
                    value={monthYear || ""}
                    onChange={(e) => setMonthYear(e.target.value)}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Admission Fee</label>
                  <input
                    type="number"
                    value={admissionAgreement.amount == null ? '' : admissionFeeAmount}
                    placeholder="Fee not configured"
                    disabled
                    style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>First Month Fee</label>
                  <input
                    type="number"
                    aria-label="Monthly fee amount"
                    value={monthlyAgreement.amount == null ? '' : monthlyFeeAmount}
                    placeholder="Fee not configured"
                    disabled
                    style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Late Fee (₹20/day after 12th; stops at fee month end)</label>
                  <input aria-label="Late fee" type="number" value={lateFee} disabled style={{ width: '100%', padding: '0.5rem' }} />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Discount</label>
                  <input
                    type="number"
                    value={monthlyDiscount}
                    onChange={(e) => setMonthlyDiscount(Number(e.target.value))}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <strong>
                    Total Payable: {feeSetupRequired ? 'Awaiting fee confirmation' : <>Rs.{Number(Math.max(
                      (admissionFeeAmount || 0) + (monthlyFeeAmount || 0) + (lateFee || 0) - (monthlyDiscount || 0),
                      0
                    )).toFixed(2)}</>}
                  </strong>
                </div>
              </>
            )}

            {selectedPaymentType === "OneTime" && hasOnetimeFee && (
              <>
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Workshop<select aria-label="Workshop" value={workshopId} onChange={e=>{setWorkshopId(e.target.value);setOnetimeAmount(workshops.find(c=>String(c._id)===e.target.value)?.onetimeFee ?? 0);setOnetimeDiscount(0);}}>{workshops.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}</select></label>
                  <label>Workshop Fee</label>
                  <input
                    type="number"
                    value={onetimeAmount}
                    disabled
                    style={{ width: "100%", padding: "0.5rem", backgroundColor: "#eee" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label>Discount</label>
                  <input
                    type="number"
                    value={onetimeDiscount}
                    onChange={(e) => setOnetimeDiscount(Number(e.target.value))}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <strong>
                    Total Payable: Rs.{Math.max((onetimeAmount || 0) - (onetimeDiscount || 0), 0)}
                  </strong>
                </div>
              </>
            )}

            {selectedPaymentType === "Miscellaneous" && (
              <>
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label htmlFor="miscAmount" style={{ display: "block", marginBottom: "0.5rem" }}>Amount</label>
                  <input
                    type="number"
                    id="miscAmount"
                    value={miscAmount}
                    onChange={(e) => setMiscAmount(Number(e.target.value))}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label htmlFor="miscNote" style={{ display: "block", marginBottom: "0.5rem" }}>Description / Notes</label>
                  <input
                    type="text"
                    id="miscNote"
                    value={miscNote}
                    onChange={(e) => setMiscNote(e.target.value)}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label htmlFor="miscDiscount" style={{ display: "block", marginBottom: "0.5rem" }}>Discount</label>
                  <input
                    type="number"
                    id="miscDiscount"
                    value={miscDiscount}
                    onChange={(e) => setMiscDiscount(Number(e.target.value))}
                    style={{ width: "100%", padding: "0.5rem" }}
                  />
                </div>

                {(miscAmount > 0 && miscNote.trim()) && (
                  <div className="form-group" style={{ marginBottom: "1rem" }}>
                    <strong>Total Payable: Rs.{Math.max((miscAmount || 0) - (miscDiscount || 0), 0)}</strong>
                  </div>
                )}
              </>
            )}

            {selectedPaymentType === 'Event' && selectedEvent && <EventPaymentHistory state={eventHistory} />}
            {selectedPaymentType === "Event" && (
              <>
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label htmlFor="event" style={{ display: "block", marginBottom: "0.5rem" }}>Select Event</label>
                  <select
                    id="event"
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    style={{ width: "100%", padding: "0.5rem" }}
                  >
                    <option value="">Choose an event</option>
                    {events.map((event) => (
                      <option key={event._id} value={event._id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedEvent && (
                  <>
                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                      <label style={{ display: "block", marginBottom: "0.5rem" }}>Event Fee</label>
                      <input
  type="number"
  aria-label="Event payment amount"
  step="0.01"
  value={eventFee}
  onChange={(e) => setEventFee(Number(e.target.value) || 0)}
  min={0}
  style={{ width: "100%", padding: "0.5rem" }}
/>
                    </div>

                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                      <label htmlFor="eventDiscount" style={{ display: "block", marginBottom: "0.5rem" }}>Discount</label>
                      <input
                        type="number"
                        id="eventDiscount"
                        step="0.01"
                        value={eventDiscount}
                        onChange={(e) => setEventDiscount(Number(e.target.value))}
                        style={{ width: "100%", padding: "0.5rem" }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: "1rem" }}>
  <label htmlFor="eventNote" style={{ display: "block", marginBottom: "0.5rem" }}>Note</label>
  <input
    type="text"
    id="eventNote"
    value={eventNote}
    onChange={(e) => setEventNote(e.target.value)}
    placeholder="e.g., Event Fee (cash)"
    style={{ width: "100%", padding: "0.5rem" }}
  />
</div>

                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                      <strong>Total Payable: Rs.{Math.max(eventFee - (eventDiscount || 0), 0)}</strong>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        <div className="form-group" style={{ marginBottom: "1rem" }}>
          <label htmlFor="paymentDate" style={{ display: "block", marginBottom: "0.5rem" }}>Payment Date</label>
          <input
            type="date"
            id="paymentDate"
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            style={{ width: "100%", padding: "0.5rem", borderBottom: "2px solid #ccc" }}
          />
          <PaymentDateWarning date={paymentDate}/>
        </div>
        <div className="form-group" style={{ marginBottom: "1rem" }}>
  <label htmlFor="paymentMode" style={{ display: "block", marginBottom: "0.5rem" }}>
    Payment Mode <span style={{color:'#d00'}}>*</span>
  </label>
  <select
    id="paymentMode"
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
    required
    style={{ width: "100%", padding: "0.5rem" }}
  >
    <option value="UPI">UPI</option>
    <option value="Cash">Cash</option>
    <option value="Card">Card</option>
    <option value="NetBanking">NetBanking</option>
    <option value="Others">Others</option>
  </select>
</div>

        <button type="submit" disabled={feeSetupRequired} style={{ width: "100%", padding: "0.75rem", backgroundColor: "#007bff", color: "#fff", border: "none", borderRadius: "4px" }}>
          Submit Payment
        </button>
      </form>
    {/* Confirmation Modal: moved here, outside all payment-type conditionals */}
    {showConfirm && confirmData && (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6">
        <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
          <div className="px-5 py-4 border-b">
            <h3 className="text-lg font-semibold">Confirm Payment</h3>
            <p className="text-xs text-gray-500">Please review before submitting.</p>
          </div>

          <div className="px-5 py-4 space-y-3 text-sm">
            <PaymentDateWarning date={paymentDate}/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><span className="text-gray-500">Student:</span><br/>
                <span className="font-medium">{studentLabel(studentFeeDetails || selectedStudent)}</span>
              </div>
              <div><span className="text-gray-500">Payment Date:</span><br/>
                <span className="font-medium">{safeDate(paymentDate)}</span>
              </div>
              <div><span className="text-gray-500">Payment Mode:</span><br/>
                <span className="font-medium">{paymentMode || 'UPI'}</span>
              </div>
              <div><span className="text-gray-500">Discount:</span><br/>
                <span className="font-medium">{fmtINR(confirmData.discountAtPayment || 0)}</span>
              </div>
            </div>

            <div className="border rounded">
              <div className="px-3 py-2 bg-gray-50 font-medium">Fee Lines</div>
              <div className="max-h-60 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Note / Event</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(confirmData.feeLines || []).map((fl, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 capitalize">
                          {fl.type === 'monthlyFee' ? 'Monthly' :
                           fl.type === 'admissionFee' ? 'Admission' :
                           fl.type === 'eventFee' ? 'Event' :
                           (fl.type === 'oneTimeFee' || fl.type === 'onetimeFee' || fl.type === 'onetimeCourseFee') ? 'One-time Course' :
                           fl.type === 'miscFee' ? 'Misc' :
                           fl.type === 'lateFee' ? 'Late Fee' : fl.type}
                          {fl.month ? ` (${fl.month})` : ''}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {fl.eventId ? `Event: ${events.find(e => e._id === fl.eventId)?.name || ''}` : (fl.note || '—')}
                        </td>
                        <td className="px-3 py-2 text-right">{fmtINR(fl.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 border-t">
                      <td className="px-3 py-2 font-medium" colSpan={2}>Gross</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtINR(confirmData.gross)}</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2" colSpan={2}>Discount</td>
                      <td className="px-3 py-2 text-right">− {fmtINR(confirmData.discountAtPayment || 0)}</td>
                    </tr>
                    <tr className="bg-gray-100 border-t">
                      <td className="px-3 py-2 font-semibold" colSpan={2}>Total Payable</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtINR(confirmData.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {selectedStudent?.status === 'inactive' && (
              <div className="text-red-600 text-xs">
                This student is inactive. Payments are usually not allowed.
              </div>
            )}
          </div>

          <div className="px-5 py-4 bg-gray-50 flex justify-end gap-3">
            <button type="button" className="px-4 py-2 rounded border" onClick={cancelConfirm}>Cancel</button>
            <button type="button" className="px-4 py-2 rounded bg-indigo-600 text-white" onClick={confirmAndSubmit} disabled={isSubmitting}>
              Confirm &amp; Submit
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default AddPaymentForm;
